<script setup>
import { computed, useAttrs } from 'vue';

/**
 * Table header cell for use in custom header rows inside HrTable.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Rendering a custom header cell in a table built with HrTable
 * @within hr-table
 * @a11y A sortable column is a tab stop, sorts on Enter or Space, and reports its direction with
 *   aria-sort. No role is added: a th already is a columnheader, and aria-sort is only valid on
 *   one, so the column announces as a sorted header rather than as a button. A cell with no
 *   column scope, or a column that is not sortable, is left untouched.
 * @see https://quasar.dev/vue-components/table Quasar q-th API
 */
// inheritAttrs: false because the template spreads $attrs onto the q-th itself. Without it
// Vue's own fallthrough applies them a second time.
defineOptions({ name: 'HrTh', inheritAttrs: false });

const ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar'];

const props = defineProps({
  /**
   * The column this cell belongs to, by name.
   *
   * Quasar resolves a cell's column from its own vnode key, and `key` is consumed by Vue on THIS
   * component rather than passed down — so without it a cell in a custom header or body row cannot
   * find its column. QTh throws reading `sortable` off nothing; QTd renders an empty cell and says
   * nothing at all. Set it to `col.name` alongside the `:key` the v-for needs.
   */
  columnName: {
    type: String,
    default: null,
  },
});

const attrs = useAttrs();

/*
 * The same two ways round QTh resolves it, so the sort state below describes the column the cell
 * actually renders: by name out of the column map when the caller names one, otherwise the single
 * column the header scope carries. Absent for a header cell built by hand outside a table.
 */
const col = computed(() => {
  const scope = attrs.props;

  if (!scope) {
    return null;
  }

  return (props.columnName !== null ? scope.colsMap?.[props.columnName] : scope.col) ?? null;
});

const isSortable = computed(() => col.value?.sortable === true);

/*
 * Quasar records the active sort only in the class it builds for the cell - the header slot scope
 * carries cols, sort and colsMap, but not the pagination that says which column is sorted. This
 * reads the same field QTh itself reads, so the two cannot disagree.
 */
const ariaSort = computed(() => {
  if (!isSortable.value) {
    return undefined;
  }

  // eslint-disable-next-line no-underscore-dangle -- Quasar's own field name for the cell class.
  const classes = ` ${col.value.__thClass ?? ''} `;

  if (!classes.includes(' sorted ')) {
    return 'none';
  }

  return classes.includes(' sort-desc ') ? 'descending' : 'ascending';
});

/*
 * Dispatching a click rather than calling the scope's sort() keeps the keyboard on the mouse's
 * path: Quasar's one handler on the cell sorts and then re-emits click, so a caller listening for
 * a click on the header goes on hearing it from the keyboard too. Same reasoning as HrChip.
 *
 * Only when the cell itself holds focus - a control inside a header cell activates itself, and
 * must not sort the column as well.
 */
function onKeydown(event) {
  if (event.target !== event.currentTarget || !ACTIVATION_KEYS.includes(event.key)) {
    return;
  }

  if (event.key !== 'Enter') {
    // Space scrolls the page on keydown and repeats while held; a button does neither.
    event.preventDefault();

    if (event.repeat === true) {
      return;
    }
  }

  event.currentTarget.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
}

/*
 * $attrs last, so a caller's own tabindex or aria-sort wins. The remaining Quasar props -
 * `props`, `auto-width`, `colspan` - travel this way too, which is what a primitive wrapping a
 * Quasar component is for.
 */
const effectiveAttrs = computed(() => ({
  ...(isSortable.value ? { tabindex: 0, 'aria-sort': ariaSort.value, onKeydown } : {}),
  ...attrs,
}));
</script>

<template>
  <!-- The remaining Quasar props - `props`, `auto-width`, `colspan` - travel by $attrs,
       which is what a primitive wrapping a Quasar component is for. -->
  <q-th
    :key="props.columnName"
    v-bind="effectiveAttrs"
    :class="{ 'hr-th--sortable': isSortable }"
  >
    <!-- @slot Header cell content. -->
    <slot />
  </q-th>
</template>

<style lang="scss" scoped>
/*
 * Keyboard only: :focus-visible does not match a pointer click on a cell that now carries a
 * tabindex, so a mouse user sees what they saw before. Inset, because a header cell has no corner
 * radius and sits flush against its neighbours inside a scrolling table.
 */
.hr-th--sortable:focus-visible {
  outline: 2px solid var(--app-primary);
  outline-offset: -2px;
}

/* Quasar reveals the sort arrow on hover; focus has to reveal it too, or the ring explains nothing. */
.hr-th--sortable:focus-visible :deep(.q-table__sort-icon) {
  opacity: 0.64;
}
</style>
